import useSWR from "swr";

import { pushNotification } from "../stores/hooks/useToasts";
import { reduxStore } from "../stores/reduxStore";
import { Token, authSlice } from "../stores/slices/authSlice";
import { loadScript } from "../util/loadScript";
import { parseToken } from "../util/parseToken";

let pendingUpgrade: Promise<Token> | null = null;
let isSessionExpired = false;
const unverifiedTokenIds = new Set<string>();

// The refresh route mirrors the token the page load minted (code/server/tokenRoutes.js):
// a dealer site refreshes by its url code, a national or dealer-group site by its own
// code. Before TW-1122 every non-dealer site requested /unknown/token.js, which Express
// forwarded to the API as a dealer named "unknown", and the hourly refresh failed.
// A site with no account at all (the consumer site) has nothing to refresh against.
export const getTokenRefreshPath = (): null | string => {
  const dealerUrlCode = window.dealerAccountInfoPayload?.dealerUrlCode;
  if (dealerUrlCode) {
    return `/${encodeURIComponent(dealerUrlCode)}/token.js`;
  }
  const nationalCode =
    window.nationalAccountInfoPayload?.nationalAccountUrlCode;
  if (nationalCode) {
    return `/national/${encodeURIComponent(nationalCode)}/token.js`;
  }
  const groupCode = window.groupAccountInfoPayload?.dealerGroupUrlCode;
  if (groupCode) {
    return `/group/${encodeURIComponent(groupCode)}/token.js`;
  }
  return null;
};

const loadToken = async (): Promise<Token> => {
  const path = getTokenRefreshPath();
  if (!path) {
    throw new Error("No account to refresh the token for");
  }

  await loadScript("tw-token", path);

  const token = parseToken();
  if (!token) {
    throw new Error("Token is not valid");
  }
  return token;
};

const fetchToken = (): Promise<Token> => {
  if (!pendingUpgrade) {
    pendingUpgrade = loadToken().finally(() => {
      pendingUpgrade = null;
    });
  }
  return pendingUpgrade;
};

const replaceToken = (token: Token): void => {
  const action = authSlice.actions.replaceToken(token);
  reduxStore.dispatch(action);
};

export const expireSession = (): void => {
  if (isSessionExpired) {
    return;
  }
  isSessionExpired = true;
  const { auth } = reduxStore.getState();
  replaceToken({
    ...auth,
    tokenIsAuthenticated: false,
  });
  pushNotification({
    message: "toast.sessionExpired",
  });
};

export const restoreSession = (): void => {
  isSessionExpired = false;
};

export const verifyToken = (tokenId: string): void => {
  unverifiedTokenIds.delete(tokenId);
};

// One re-mint per rejected token: a 403 on a token that was itself just minted and never
// accepted ends the session instead of minting again, and fetcher.ts retries a request
// once at most. This is what keeps a 403 to an authenticated token from looping.
export const upgradeToken = async (
  rejectedTokenId: string
): Promise<Token | null> => {
  if (unverifiedTokenIds.has(rejectedTokenId)) {
    expireSession();
    return null;
  }
  if (isSessionExpired) {
    return null;
  }

  const token = await fetchToken().catch(() => null);
  if (!token || isSessionExpired) {
    return null;
  }

  unverifiedTokenIds.add(token.tokenId);
  replaceToken(token);
  return token;
};

const refreshToken = async (): Promise<Token | null> => {
  const token = await fetchToken().catch(() => null);
  if (token) {
    replaceToken(token);
    restoreSession();
  }
  return token;
};

export const useUpgradeToken = (): void => {
  useSWR("upgradeToken", refreshToken, {
    refreshInterval: 1000 * 60 * 60,
    revalidateOnFocus: false,
    revalidateOnMount: false,
  });
};
